<script setup lang="ts">
import { FilterDismissIcon, SaveIcon, SparkleIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { NxChip } from '../Chip';
import { i18nInjectionKey } from '../internal';
import {
  countGroupConditions,
  defaultFilterOperator,
  filterOperatorArity,
  isConditionComplete,
  type NxTableColumnFilter,
  type NxTableFilterCondition,
  type NxTableFilterGroup,
  type NxTableFilterKind,
} from './filterModel';

/**
 * "Aktive Filter" chips: one removable chip per active condition (quick
 * filter-row values, popover conditions, wizard filter).
 *
 * Layouts: `bar` is the standalone bar with save / reset-all actions;
 * `menu` renders only label + chips as a panel inside the gear dropdown —
 * the actions live as regular menu items there, and the overlay placement
 * means the table below never jumps when filters appear/disappear.
 */
const props = defineProps<{
  quickFilters: ReadonlyMap<string, readonly unknown[]>;
  columnFilters: ReadonlyMap<string, NxTableColumnFilter>;
  advancedFilter: NxTableFilterGroup | null;
  columnMeta: (columnId: string) => {
    displayName: string;
    kind: NxTableFilterKind;
    optionLabel?: (value: string) => string;
  };
  layout?: 'bar' | 'menu';
}>();

const emit = defineEmits<{
  'remove-quick': [columnId: string];
  'remove-condition': [columnId: string, index: number];
  'clear-advanced': [];
  'open-advanced': [];
  reset: [];
  save: [];
}>();

const i18n = inject(i18nInjectionKey)!;

type ChipColumnMeta = {
  displayName: string;
  kind: NxTableFilterKind;
  optionLabel?: (value: string) => string;
};

function formatValue(meta: ChipColumnMeta, value: string | undefined): string {
  if (value === undefined || value === '') return '';
  if (meta.kind === 'date') {
    const t = Date.parse(value);
    if (Number.isFinite(t)) return i18n.d(new Date(t), 'short');
  }
  if (meta.kind === 'bool') {
    return value === 'true'
      ? i18n.t('common.table.filter.bool_true')
      : i18n.t('common.table.filter.bool_false');
  }
  if (meta.kind === 'select' && meta.optionLabel) return meta.optionLabel(value);
  return value;
}

function conditionLabel(meta: ChipColumnMeta, condition: NxTableFilterCondition): string {
  const op = i18n.t(`common.table.filter.op.${condition.operator}`);
  switch (filterOperatorArity(condition.operator)) {
    case 'none':
      return `${meta.displayName} ${op}`;
    case 'range': {
      const from = formatValue(meta, condition.value);
      const to = formatValue(meta, condition.valueTo);
      if (from && to) return `${meta.displayName} ${op} ${from} – ${to}`;
      if (from) return `${meta.displayName} ≥ ${from}`;
      return `${meta.displayName} ≤ ${to}`;
    }
    case 'multi':
      return `${meta.displayName} ${op} ${(condition.values ?? [])
        .map((v) => formatValue(meta, v))
        .join(', ')}`;
    default:
      return `${meta.displayName} ${op} ${formatValue(meta, condition.value)}`;
  }
}

const quickChips = computed(() =>
  [...props.quickFilters.entries()]
    .filter(([, values]) => values.length > 0)
    .map(([columnId, values]) => {
      const meta = props.columnMeta(columnId);
      const op = i18n.t(`common.table.filter.op.${defaultFilterOperator(meta.kind)}`);
      const text = values.map((v) => formatValue(meta, String(v))).join(', ');
      return { columnId, label: `${meta.displayName} ${op} ${text}` };
    })
);

const conditionChips = computed(() =>
  [...props.columnFilters.entries()].flatMap(([columnId, filter]) => {
    const meta = props.columnMeta(columnId);
    return filter.conditions.flatMap((condition, index) =>
      isConditionComplete(condition)
        ? [{ columnId, index, label: conditionLabel(meta, condition) }]
        : []
    );
  })
);

const advancedCount = computed(() => countGroupConditions(props.advancedFilter));

const hasAny = computed(
  () => quickChips.value.length > 0 || conditionChips.value.length > 0 || advancedCount.value > 0
);
</script>

<template>
  <div v-if="hasAny" class="filter-bar" :class="{ 'layout-menu': props.layout === 'menu' }">
    <span class="bar-label">{{ i18n.t('common.table.filter.active_filters') }}</span>

    <NxChip
      v-for="chip in quickChips"
      :key="`quick-${chip.columnId}`"
      removable
      size="xs"
      :remove-label="i18n.t('common.table.filter.remove_filter')"
      @remove="emit('remove-quick', chip.columnId)"
    >
      {{ chip.label }}
    </NxChip>

    <NxChip
      v-for="chip in conditionChips"
      :key="`cond-${chip.columnId}-${chip.index}`"
      removable
      size="xs"
      :remove-label="i18n.t('common.table.filter.remove_filter')"
      @remove="emit('remove-condition', chip.columnId, chip.index)"
    >
      {{ chip.label }}
    </NxChip>

    <NxChip
      v-if="advancedCount > 0"
      removable
      selected
      size="xs"
      :remove-label="i18n.t('common.table.filter.remove_filter')"
      @click="emit('open-advanced')"
      @remove="emit('clear-advanced')"
    >
      <SparkleIcon />
      {{ i18n.t('common.table.filter.advanced_chip', { count: advancedCount }) }}
    </NxChip>

    <template v-if="props.layout !== 'menu'">
      <span class="bar-spacer"></span>

      <button type="button" class="bar-action" @click="emit('save')">
        <SaveIcon />
        {{ i18n.t('common.table.filter.save_current') }}
      </button>
      <button type="button" class="bar-action" @click="emit('reset')">
        <FilterDismissIcon />
        {{ i18n.t('common.table.filter.reset_all') }}
      </button>
    </template>
  </div>
</template>

<style scoped>
.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-brand-subtle-bg);
  margin-bottom: var(--nxt-space-2xs);
  font-size: var(--nxt-font-size-sm);
}

.bar-label {
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg-muted);
  margin-right: var(--nxt-space-2xs);
}

/* Panel inside the gear dropdown: plain section, chips wrap in a bounded box. */
.filter-bar.layout-menu {
  border: none;
  background: transparent;
  margin-bottom: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  max-width: var(--nxt-panel-width-xl);
}

.filter-bar.layout-menu .bar-label {
  flex-basis: 100%;
}

.bar-spacer {
  flex: 1 1 auto;
}

.bar-action {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-2xs);
  border: none;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: var(--nxt-surface-bg);
  }
}
</style>
